import { Stack } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";

/** Medication row shape from getMedsTherapiesRowValues / getPostpartumMedsTherapiesRowValues / getPostpartumHemorrhageRowValues */
export interface LaborMedTherapyMedicationRow {
  medication: string | undefined;
  form: string | undefined;
  duration: string | undefined;
  xDays: string | undefined;
  dosage: string | undefined;
  frequency: string | undefined;
  prn: string | undefined;
  specialInstructions: string | undefined;
}

export interface LaborMedTherapyTherapyRow {
  therapy: string | undefined;
  reason: string | undefined;
}

export interface LaborMedTherapyManeuverRow {
  maneuver: string | undefined;
}

const MEDICATION_DETAIL_FIELDS = [
  { key: "form", label: "Form" },
  { key: "dosage", label: "Dosage" },
  { key: "frequency", label: "Frequency" },
  { key: "duration", label: "Duration" },
  { key: "prn", label: "PRN" },
  { key: "specialInstructions", label: "Special Instructions" },
] as const;

type MedicationDetailFieldKey = (typeof MEDICATION_DETAIL_FIELDS)[number]["key"];

/** Medication name plus each labeled row in the structured diff. */
export type LaborMedTherapyMedicationFieldKey = "medication" | MedicationDetailFieldKey;

function medicationFieldValue(
  medicationRow: LaborMedTherapyMedicationRow | undefined,
  field: LaborMedTherapyMedicationFieldKey,
): string {
  if (!medicationRow) return "";
  switch (field) {
    case "medication":
      return medicationRow.medication ?? "";
    case "form":
      return medicationRow.form ?? "";
    case "dosage":
      return medicationRow.dosage ?? "";
    case "frequency":
      return medicationRow.frequency ?? "";
    case "duration": {
      const durationValue = medicationRow.duration;
      if (durationValue === "X days" && medicationRow.xDays) {
        return `${medicationRow.xDays} Days`;
      }
      return durationValue ?? "";
    }
    case "prn":
      return medicationRow.prn ?? "";
    case "specialInstructions":
      return medicationRow.specialInstructions ?? "";
    default: {
      const exhaustiveField: never = field;
      return exhaustiveField;
    }
  }
}

function therapyFieldValue(
  therapyRow: LaborMedTherapyTherapyRow | undefined,
  field: "therapy" | "reason",
): string {
  if (!therapyRow) return "";
  return therapyRow[field] ?? "";
}

function maneuverTypeValue(maneuverRow: LaborMedTherapyManeuverRow | undefined): string {
  return maneuverRow?.maneuver ?? "";
}

interface LaborObservationMedTherapySectionsProps {
  currentManeuvers?: LaborMedTherapyManeuverRow[];
  currentMedications: LaborMedTherapyMedicationRow[];
  currentTherapies: LaborMedTherapyTherapyRow[];
  /** Postpartum Hemorrhage includes maneuvers; labor/client meds forms do not */
  includeManeuvers: boolean;
  previousManeuvers?: LaborMedTherapyManeuverRow[];
  previousMedications: LaborMedTherapyMedicationRow[];
  previousTherapies: LaborMedTherapyTherapyRow[];
  variant: "previous" | "amended";
}

/**
 * Per-field WordsDiff blocks for medications, therapies, and optional PPH maneuvers.
 * Matches flowsheet row labels and Postpartum Encounter medication/therapy diff structure.
 */
export function LaborObservationMedTherapySections({
  currentManeuvers = [],
  currentMedications,
  currentTherapies,
  includeManeuvers,
  previousManeuvers = [],
  previousMedications,
  previousTherapies,
  variant,
}: LaborObservationMedTherapySectionsProps) {
  const showRemoved = variant === "previous";

  const maxMedicationCount = Math.max(currentMedications.length, previousMedications.length);
  const maxTherapyCount = Math.max(currentTherapies.length, previousTherapies.length);
  const maxManeuverCount = includeManeuvers
    ? Math.max(currentManeuvers.length, previousManeuvers.length)
    : 0;

  return (
    <Stack gap="md">
      {maxMedicationCount > 0 && (
        <Stack gap="xs">
          <TextWrapper fw={600}>Medications</TextWrapper>
          {Array.from({ length: maxMedicationCount }, (_, index) => (
            <Stack ml="md" gap="xs" key={`med-${index}`}>
              <Stack gap="0">
                <TextWrapper fw={600}>Medication {index + 1}</TextWrapper>
                <WordsDiff
                  showRemoved={showRemoved}
                  current={medicationFieldValue(currentMedications[index], "medication")}
                  previous={medicationFieldValue(previousMedications[index], "medication")}
                />
              </Stack>
              {MEDICATION_DETAIL_FIELDS.map(({ key, label }) => (
                <Stack gap="0" key={`${index}-${key}`}>
                  <TextWrapper fw={600}>{label}</TextWrapper>
                  <WordsDiff
                    showRemoved={showRemoved}
                    current={medicationFieldValue(currentMedications[index], key)}
                    previous={medicationFieldValue(previousMedications[index], key)}
                  />
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
      )}

      {maxTherapyCount > 0 && (
        <Stack gap="xs">
          <TextWrapper fw={600}>Therapy</TextWrapper>
          {Array.from({ length: maxTherapyCount }, (_, index) => (
            <Stack ml="md" gap="xs" key={`therapy-${index}`}>
              <Stack gap="0">
                <TextWrapper fw={600}>Therapy {index + 1}</TextWrapper>
                <WordsDiff
                  showRemoved={showRemoved}
                  current={therapyFieldValue(currentTherapies[index], "therapy")}
                  previous={therapyFieldValue(previousTherapies[index], "therapy")}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Reason</TextWrapper>
                <WordsDiff
                  showRemoved={showRemoved}
                  current={therapyFieldValue(currentTherapies[index], "reason")}
                  previous={therapyFieldValue(previousTherapies[index], "reason")}
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      {includeManeuvers && maxManeuverCount > 0 && (
        <Stack gap="xs">
          <TextWrapper fw={600}>Maneuvers</TextWrapper>
          {Array.from({ length: maxManeuverCount }, (_, index) => (
            <Stack ml="md" gap="xs" key={`maneuver-${index}`}>
              <TextWrapper fw={600}>Maneuver {index + 1}</TextWrapper>
              <Stack gap="0">
                <TextWrapper fw={600}>Type</TextWrapper>
                <WordsDiff
                  showRemoved={showRemoved}
                  current={maneuverTypeValue(currentManeuvers[index])}
                  previous={maneuverTypeValue(previousManeuvers[index])}
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
